import {
  Button,
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@sea-snag/ui';
import { useQuery } from '@tanstack/react-query';
import { Camera, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { marketDataApi } from '../lib/api-client';

interface BoatImageGalleryProps {
  boatPid: string;
  boatName: string;
  imageCount: number;
}

export function BoatImageGallery({ boatPid, boatName, imageCount }: BoatImageGalleryProps) {
  const [open, setOpen] = useState(false);
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  const { data: images, isLoading } = useQuery({
    queryKey: ['market-boat-images', boatPid],
    queryFn: () => marketDataApi.images(boatPid),
    enabled: open,
  });

  useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    api.on('select', onSelect);
    return () => {
      api.off('select', onSelect);
    };
  }, [api]);

  if (imageCount === 0) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={(e) => e.stopPropagation()}
        >
          <Camera className="h-4 w-4 text-muted-foreground" />
        </Button>
      </DialogTrigger>
      <DialogContent
        showCloseButton={false}
        className="!max-w-none !border-0 !bg-transparent !p-0 !ring-0 w-[92vw] sm:w-auto"
      >
        <DialogTitle className="sr-only">{boatName}</DialogTitle>
        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="text-muted h-8 w-8 animate-spin" />
          </div>
        ) : images && images.length > 0 ? (
          <div className="px-14">
            <Carousel setApi={setApi} className="w-full">
              <CarouselContent>
                {images.map((image) => (
                  <CarouselItem key={image.id}>
                    <img
                      src={image.url}
                      alt={image.name}
                      className="mx-auto max-h-[85vh] w-auto max-w-full rounded-lg object-contain"
                      loading="lazy"
                    />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="!bg-background/80 hover:!bg-background h-10 w-10" />
              <CarouselNext className="!bg-background/80 hover:!bg-background h-10 w-10" />
            </Carousel>
            <p className="text-background/90 mt-2 text-center text-sm drop-shadow">
              {boatName} — {current + 1} / {images.length}
            </p>
          </div>
        ) : (
          <p className="text-background py-8 text-center">No images available</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
